import React from 'react';
import cn from 'classnames';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextIconSpacing } from '@react-md/icon';
import { Button } from '@react-md/button';
import { DropdownMenu, MenuItemLink } from '@react-md/menu';
import { BELOW_INNER_LEFT_ANCHOR } from '@react-md/utils';
import { useAppSelector } from '@app/hooks';
import { selectUserPermissions } from '@modules/user';
import { Icons } from '@components/icons';
import { ButtonLink } from '@components/link';
import { FeatureContent } from '@components/feature-content';
import { getNodeIdComposer, reverse } from '@utils';
import { useBuildLocation } from '@hooks';

import styles from './browse-files.module.scss';

const _id = getNodeIdComposer('browse_files');
const links = { inventory: { id: _id('inventory', 'link') }, uploads: { id: _id('recent_uploads', 'link') } };

interface BrowseFilesProps {
    isAuthenticated: boolean;
    onClick: () => void;
    className?: string;
    isLoading?: boolean;
}

export const BrowseFiles: React.FC<BrowseFilesProps> = ({ isAuthenticated, onClick, className ,isLoading = false}) => {
    const { isInventoryPageAllowed } = useAppSelector(selectUserPermissions);

    const modelsLocation = useBuildLocation({
        to: reverse('userModels'),
        setFromLocation: true, // todo for back button? need to check
    });
    const inventoryLocation = useBuildLocation({
        to: reverse('widgetInventory'),
        setFromLocation: true,
    });

    const { t } = useTranslation();

    const menuActions = [
        isInventoryPageAllowed && (
            <MenuItemLink key={links.inventory.id} id={links.inventory.id} component={NavLink} {...inventoryLocation}>
                <FeatureContent contentKey={links.inventory.id} fallback={t('links.inventory', 'Inventory')} />
            </MenuItemLink>
        ),
        <MenuItemLink key={links.uploads.id} id={links.uploads.id} component={NavLink} {...modelsLocation}>
            <FeatureContent contentKey={links.uploads.id} fallback={t('links.recentUploads', 'Recent uploads')} />
        </MenuItemLink>,
    ].filter(Boolean);

    const shouldUseMenu = isInventoryPageAllowed;

    return (
        <div className={cn(styles.box, className)}>
            <div className={cn('emulated-flex-gap', 'justify-center', 'items-center', styles.actions)}>
                <FeatureContent
                    contentKey={_id('upload_files', 'button')}
                    fallback={t('buttons.browseFiles', 'Browse files')}
                    wrapper={props => (
                        <Button theme="primary" themeType="outline" onClick={onClick} disabled={isLoading} {...props} />
                    )}
                />

                {shouldUseMenu ? (
                    <FeatureContent
                        contentKey={_id('library', 'button')}
                        fallback={t('buttons.library', 'Library')}
                        wrapper={props => (
                            <DropdownMenu
                                id={props.id!}
                                className={cn('rmd-button--flat', 'rmd-button--underlined')}
                                menuClassName={styles.menu}
                                children={menuActions}
                                buttonChildren={
                                    <TextIconSpacing icon={<Icons.Inbox />} forceIconWrap>
                                        {props.children}
                                    </TextIconSpacing>
                                }
                                anchor={BELOW_INNER_LEFT_ANCHOR}
                                disableRipple
                                portal
                            />
                        )}
                    />
                ) : (
                    isAuthenticated && (
                        <ButtonLink
                            id={links.uploads.id}
                            className={cn('rmd-button--flat', 'rmd-button--underlined')}
                            disabled={isLoading}
                            {...modelsLocation}
                        >
                            <TextIconSpacing icon={<Icons.Inbox />} forceIconWrap>
                                <FeatureContent
                                    contentKey={links.uploads.id}
                                    fallback={t('links.recentUploads', 'Recent uploads')}
                                />
                            </TextIconSpacing>
                        </ButtonLink>
                    )
                )}
            </div>
        </div>
    );
};
